/**
 * BlueSphere design tokens.
 *
 * These are not invented for MDR. They are the values the BlueSphere
 * portal actually ships (`bluesphere-portal/tailwind.config.ts` and
 * `app/globals.css`), reproduced here so this product reads as part of
 * the family rather than as a cousin that nearly matches.
 *
 * The portal is the authority where it and the React Native app disagree:
 * it holds the only structured token layer, and its palette is the more
 * recently revised of the two.
 *
 * Four things carry the identity, in rough order of how loudly:
 *
 *   1. A navy-to-black gradient canvas with LIGHT cards on top. The
 *      inversion is the strongest cue — the page is dark, every panel is
 *      near-white with black text.
 *   2. Brand blue #76B7CD. It is the secondary CTA, the focus ring, the
 *      "normal" risk band, and the logo's primary color.
 *   3. Everything is a pill. Buttons, nav, tabs, badges and list rows are
 *      fully rounded; panels are 24px.
 *   4. Lexend, at every weight.
 */

export const colors = {
  // ---- Canvas and surfaces ----
  // #2A3241 is THE BlueSphere navy: the gradient's light end, the PWA
  // theme color, and the portal's --primary.
  bg: '#2A3241',
  bgDeep: '#0B0B0B', // the gradient's far end
  surface: '#EDEDED', // cards stay light on the dark canvas, by design
  surfaceBright: '#FFFFFF',
  surfaceAlt: '#E4F1F5', // pale blue — the portal's accent surface
  surfaceMuted: '#EFF3F6',

  // ---- Brand ----
  primary: '#2A3241',
  primaryFg: '#FFFFFF',
  secondary: '#76B7CD', // brand blue: the CTA, the focus ring, "normal"
  secondaryFg: '#2A3241',
  secondaryMuted: '#BBDBE6',

  // ---- Text ----
  textOnDark: '#FFFFFF',
  textOnLight: '#2A3241',
  textSecondary: '#374046',
  muted: '#6B7280',

  // ---- Lines ----
  border: '#D7DADF',
  borderMuted: '#C5CBD5',

  // ---- Risk bands ----
  // The portal's current ramp. MDR's previous values were a desaturated
  // near-miss of these (#5B7DB1 / #E0A946 / #E0703B / #D04A3C), which is
  // most of why the product did not look like BlueSphere.
  scoreNormal: '#76B7CD',
  scoreElevated: '#F9CC00',
  scoreHigh: '#F5831A',
  scoreCritical: '#E32A0A',

  // ---- Status ----
  resolved: '#10B981',
  inProcess: '#3B82F6',
  success: '#00B788',
  warning: '#F4BC13',
  danger: '#D12020',

  // Translucent fills for risk-band chart backdrops. The portal draws
  // these as y-axis annotations at 10% opacity behind every score trend —
  // one of the most recognizable things it does.
  bandNormal: 'rgba(118, 183, 205, 0.10)',
  bandElevated: 'rgba(249, 204, 0, 0.10)',
  bandHigh: 'rgba(245, 131, 26, 0.10)',
  bandCritical: 'rgba(227, 42, 10, 0.10)',

  // Pastel surfaces for status pills and KPI tiles: pale background,
  // saturated text and dot of the matching hue.
  tintNormal: '#E6F0FF',
  tintElevated: '#FFF8E6',
  tintHigh: '#FFF4E6',
  tintCritical: '#FFF1F0',
} as const;

/** The signature canvas. 270deg, navy through near-black. */
export const gradients = {
  canvas:
    'linear-gradient(270deg, rgba(42,50,65,1) 40%, rgba(23,26,31,1) 79%, rgba(11,11,11,1) 100%)',
} as const;

export const radii = {
  /** Panels and modals. The portal uses rounded-3xl on every widget. */
  card: '24px',
  /** Inner cards nested inside a panel. */
  cardInner: '16px',
  /** Buttons, nav items, badges, list rows — almost everything. */
  pill: '999px',
  /** Inputs are the one deliberately square-ish element. */
  input: '6px',
} as const;

export const typography = {
  fontFamily: '"Lexend", system-ui, -apple-system, "Segoe UI", sans-serif',
  scoreFontWeight: 700,
  baseSize: '14px',
} as const;

export const shadows = {
  card: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  cardHover: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
  modal: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
} as const;

/**
 * Map a 0-100 score to its band name + colors.
 *
 * `inverted` distinguishes the two score directions this product carries,
 * and getting it wrong silently paints a critical detection calm blue:
 *
 *   - Posture/composite scores run HIGH = GOOD (100 is healthy), matching
 *     the portal's Shield and Fabric scores. That is the default.
 *   - Detection severity runs HIGH = BAD (100 is Critical). Pass
 *     `inverted: false` for those.
 */
export interface ScoreBand {
  name: 'Normal' | 'Elevated' | 'High' | 'Critical';
  color: string;
  band: string;
  tint: string;
}

export function scoreBand(score: number, inverted = true): ScoreBand {
  const s = inverted ? score : 100 - score;
  if (s >= 70)
    return {
      name: 'Normal',
      color: colors.scoreNormal,
      band: colors.bandNormal,
      tint: colors.tintNormal,
    };
  if (s >= 50)
    return {
      name: 'Elevated',
      color: colors.scoreElevated,
      band: colors.bandElevated,
      tint: colors.tintElevated,
    };
  if (s >= 30)
    return {
      name: 'High',
      color: colors.scoreHigh,
      band: colors.bandHigh,
      tint: colors.tintHigh,
    };
  return {
    name: 'Critical',
    color: colors.scoreCritical,
    band: colors.bandCritical,
    tint: colors.tintCritical,
  };
}

/**
 * Detection severity to color. MDR carries five severity names against the
 * portal's four risk bands, so Informational and Low both sit on Normal.
 */
export const SEVERITY_COLORS: Record<string, string> = {
  Informational: colors.scoreNormal,
  Low: colors.scoreNormal,
  Medium: colors.scoreElevated,
  High: colors.scoreHigh,
  Critical: colors.scoreCritical,
};

/** Pale backgrounds for severity pills, paired with SEVERITY_COLORS text. */
export const SEVERITY_TINTS: Record<string, string> = {
  Informational: colors.tintNormal,
  Low: colors.tintNormal,
  Medium: colors.tintElevated,
  High: colors.tintHigh,
  Critical: colors.tintCritical,
};
